import { AnimatePresence, motion } from 'motion/react';
import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { api, type Cuenta, type Periodo, type ResumenDiario as Resumen, type Tarifas } from '../api';
import { useEsAdmin } from '../lib/sesion';
import { mensajeDeError, useApi } from '../lib/useApi';
import { useEsCelular } from '../lib/useMedia';
import { abrir, aparecer, desplegar, fila } from '../components/movimiento';
import { Cambia, Cargando, ChipBoton, Encabezado, Icono, Kpi, Numero, Paginador, PanelError, usePaginado, Vacio, useConfirmar } from '../components/ui';
import { BarraApilada, BarraFila, Lineas, Medidor, PALETA } from '../components/graficos';
import {
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  encabezadoTabla,
  etiquetaCampo,
  filaTabla,
  miles,
  mono,
  tarjeta,
  tono,
  tonoDeEstado,
  type Tono,
  HUSO,
} from '../theme';

/**
 * El resumen para administración: qué entró, qué salió, qué hay guardado y
 * qué quedó pendiente, en el día, la semana, la quincena, el mes, el año o
 * desde siempre. La quincena trae además la cuenta para cobrarle a ZF.
 *
 * Las fechas que manda el servidor (`dia`, `anterior`, `siguiente` y el `dia`
 * de cada punto de la serie) se asumen en ISO `AAAA-MM-DD`, que es lo que
 * come `<input type="date">` sin traducción.
 */

const TONOS: readonly string[] = ['ok', 'warn', 'err', 'neu'];

/** El tono viene del servidor: si manda cualquier otra cosa, es neutro. */
const aTono = (t: string): Tono => (TONOS.includes(t) ? (t as Tono) : 'neu');

/** «2026-09-10» → «10/09», y «2026-09» → «09/26», para el eje de la serie. */
function diaCorto(iso: string): string {
  const partes = iso.split('-');
  if (partes.length === 2) return `${partes[1]}/${partes[0]!.slice(2)}`;
  return partes.length === 3 ? `${partes[2]}/${partes[1]}` : iso;
}

const PERIODOS: { periodo: Periodo; rotulo: string }[] = [
  { periodo: 'dia', rotulo: 'Día' },
  { periodo: 'semana', rotulo: 'Semana' },
  { periodo: 'quincena', rotulo: 'Quincena' },
  { periodo: 'mes', rotulo: 'Mes' },
  { periodo: 'anio', rotulo: 'Año' },
  { periodo: 'todo', rotulo: 'Todo' },
];

const fechaAR = (iso: string, opciones: Intl.DateTimeFormatOptions): string =>
  new Date(`${iso}T12:00:00-03:00`).toLocaleDateString('es-AR', { ...opciones, timeZone: HUSO });

/** El título de la pantalla según lo que abarca. */
export function tituloDe(r: Resumen): string {
  const mesAnio = fechaAR(r.desde, { month: 'long', year: 'numeric' });
  switch (r.periodo) {
    case 'dia':
      return `Resumen del ${diaLargo(r.dia)}`;
    case 'semana':
      return `Semana del ${fechaAR(r.desde, { day: 'numeric', month: 'long' })} al ${fechaAR(r.hasta, { day: 'numeric', month: 'long' })}`;
    case 'quincena':
      return `${r.desde.endsWith('-01') ? 'Primera' : 'Segunda'} quincena de ${mesAnio}`;
    case 'mes':
      return `Resumen de ${mesAnio}`;
    case 'anio':
      return `Resumen del año ${r.desde.slice(0, 4)}`;
    case 'todo':
      return `Todo, desde el ${fechaAR(r.desde, { day: 'numeric', month: 'long', year: 'numeric' })}`;
  }
}

export const pesos = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' });

/**
 * Un importe en pesos que recorre la diferencia al cambiar. Se cuenta en
 * centavos —enteros— porque `Numero` redondea los decimales a uno solo y
 * eso comería los centavos del importe final.
 */
export function Pesos({ valor }: { valor: number }) {
  return <Numero valor={Math.round(valor * 100)} formato={(n) => pesos.format(n / 100)} />;
}

export const CAMPOS_TARIFA: { clave: keyof Tarifas; rotulo: string; ayuda: string }[] = [
  { clave: 'cargaDescarga', rotulo: 'Valor carga y descarga', ayuda: 'La mitad va en la descarga, la otra mitad en la carga.' },
  { clave: 'movimientoZorra', rotulo: 'Valor movimiento con zorra', ayuda: 'Por pallet descargado.' },
  { clave: 'retrabajo', rotulo: 'Retrabajo', ayuda: 'El armado de cada capacho.' },
  { clave: 'almacenaje', rotulo: 'Almacenaje (cada 15 días)', ayuda: 'Por pallet, sobre el día con más pallets.' },
];

/** La factura renglón por renglón, con su total. */
export function TablaFactura({ f }: { f: Cuenta }) {
  // Sin `.wms-scroll-x`: la factura tiene cuatro columnas y se tiene que leer
  // entera, sin barra al costado. El concepto es lo que se achica y hace
  // renglones; los números no se parten nunca.
  const numero = (ancho: number): CSSProperties => ({
    flex: `0 0 ${ancho}px`,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  });
  const concepto: CSSProperties = { flex: 1, minWidth: 0 };
  const celular = useEsCelular();
  // En el celular las cuatro columnas no entran: cada renglón pasa a tres
  // líneas, el concepto, su detalle y «cantidad × unitario» con el importe.
  if (celular) {
    return (
      <div>
        {f.renglones.map((r, i) => (
          <motion.div key={r.concepto} {...fila(i)} style={{ ...filaTabla, flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
            <span style={{ fontWeight: 500 }}>{r.concepto}</span>
            <span style={{ fontSize: 12, color: c.texto3 }}>{r.detalle}</span>
            <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ color: c.texto2 }}>
                <Numero valor={r.cantidad} /> × <Pesos valor={r.unitario} />
              </span>
              <span style={{ fontWeight: 600 }}>
                <Pesos valor={r.importe} />
              </span>
            </span>
          </motion.div>
        ))}
        <div style={{ ...filaTabla, fontWeight: 700, justifyContent: 'space-between' }}>
          <span>Total</span>
          <span style={{ fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>
            <Pesos valor={f.total} />
          </span>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={encabezadoTabla}>
        <span style={concepto}>Concepto</span>
        <span style={numero(64)}>Cantidad</span>
        <span style={numero(110)}>Unitario</span>
        <span style={numero(130)}>Importe</span>
      </div>
      {f.renglones.map((r, i) => (
        <motion.div key={r.concepto} {...fila(i)} style={filaTabla}>
          <span style={{ ...concepto, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 500 }}>{r.concepto}</span>
            <span style={{ fontSize: 12, color: c.texto3 }}>{r.detalle}</span>
          </span>
          <span style={numero(64)}>
            <Numero valor={r.cantidad} />
          </span>
          <span style={{ ...numero(110), color: c.texto2 }}>
            <Pesos valor={r.unitario} />
          </span>
          <span style={{ ...numero(130), fontWeight: 600 }}>
            <Pesos valor={r.importe} />
          </span>
        </motion.div>
      ))}
      <div style={{ ...filaTabla, fontWeight: 700 }}>
        <span style={concepto}>Total</span>
        <span style={{ ...numero(160), fontSize: 16 }}>
          <Pesos valor={f.total} />
        </span>
      </div>
    </div>
  );
}

/**
 * La cuenta de la quincena, con las tarifas a la vista. Administración las
 * puede cambiar desde acá mismo: es donde se ve lo que cambian.
 *
 * Cada quincena se cobra con las tarifas vigentes en su primer día, y un
 * cambio rige desde el inicio de la quincena en curso: las ya pasadas —ya
 * facturadas— no se recalculan. Por eso guardar pregunta antes, con la cuenta
 * de la quincena en curso como queda.
 */
function Facturacion({ f, onCambio }: { f: NonNullable<Resumen['facturacion']>; onCambio: () => void }) {
  const esAdmin = useEsAdmin();
  const [editando, setEditando] = useState<Record<keyof Tarifas, string> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmar, dialogo] = useConfirmar();
  const historial = useApi(() => api.historialTarifas(), []);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    if (!editando) return;
    setGuardando(true);
    setError(null);
    try {
      const nuevas: Tarifas = {
        cargaDescarga: Number(editando.cargaDescarga),
        movimientoZorra: Number(editando.movimientoZorra),
        retrabajo: Number(editando.retrabajo),
        almacenaje: Number(editando.almacenaje),
      };
      // La quincena en curso, que es la primera a la que le toca el cambio:
      // puede no ser la que está en pantalla.
      const actual = await api.resumen('quincena');
      const antes = actual.facturacion;
      const despues = antes
        ? await api.simularFacturacion(nuevas, {
            palletsIngresados: actual.kpis.palletsIngresados,
            capachosEntregados: actual.kpis.capachosEntregados,
            palletsDespachados: actual.kpis.palletsDespachados,
            picoPallets: antes.pico.pallets,
          })
        : null;
      const ok = await confirmar({
        titulo: '¿Guardar las tarifas nuevas?',
        detalle: (
          <>
            Rigen desde el <strong>{diaLargo(actual.desde)}</strong>, el inicio de la quincena en curso. Las quincenas
            anteriores quedan con la tarifa que tenían.
            {antes && despues && (
              <>
                {' '}
                La {tituloDe(actual).toLowerCase()} pasa de <strong>{pesos.format(antes.total)}</strong> a{' '}
                <strong>{pesos.format(despues.total)}</strong>.
              </>
            )}
          </>
        ),
        boton: `Guardar tarifas desde el ${fechaAR(actual.desde, { day: 'numeric', month: 'numeric' })}`,
      });
      if (!ok) return;
      await api.guardarTarifas(nuevas);
      setEditando(null);
      historial.recargar();
      onCambio();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Bloque
      titulo="Facturación de la quincena"
      bajada={
        <>
          El almacenaje se cobra sobre el día con más pallets en el depósito:{' '}
          <strong style={{ color: c.texto }}>
            {diaLargo(f.pico.dia)}, con {miles(f.pico.pallets)} pallets
          </strong>
          . Se calcula con las tarifas vigentes el primer día de la quincena: un cambio rige desde la quincena en
          curso y no toca las ya pasadas.
        </>
      }
      cuerpo={false}
    >
      <TablaFactura f={f} />

      {historial.datos && historial.datos.items.length > 0 && (
        <div style={{ padding: '14px 18px 0' }}>
          <div style={etiquetaCampo}>Historial de tarifas</div>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: c.texto2 }}>
            {historial.datos.items.map((t, i) => (
              <motion.div key={i} {...fila(i)} style={{ fontVariantNumeric: 'tabular-nums' }}>
                <strong style={{ color: c.texto }}>
                  {t.vigenteDesde ? `Desde el ${fechaAR(t.vigenteDesde, { day: 'numeric', month: 'numeric', year: 'numeric' })}` : 'Desde siempre'}
                </strong>
                {' · '}
                {CAMPOS_TARIFA.map((k) => `${k.rotulo.replace(/^Valor /, '')} ${pesos.format(t[k.clave])}`).join(' · ')}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {dialogo}

      {esAdmin && (
        <div style={{ padding: '14px 18px 18px' }}>
          <AnimatePresence mode="wait" initial={false}>
          {!editando ? (
            <motion.div key="boton" {...aparecer}>
            <button
              type="button"
              style={botonSecundario}
              onClick={() =>
                setEditando({
                  cargaDescarga: String(f.tarifas.cargaDescarga),
                  movimientoZorra: String(f.tarifas.movimientoZorra),
                  retrabajo: String(f.tarifas.retrabajo),
                  almacenaje: String(f.tarifas.almacenaje),
                })
              }
            >
              <Icono nombre="edit" size={18} />
              Editar tarifas
            </button>
            </motion.div>
          ) : (
            <motion.form key="form" {...abrir(16)} onSubmit={(e) => void guardar(e)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={rejilla(200)}>
                {CAMPOS_TARIFA.map((t) => (
                  <label key={t.clave} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={etiquetaCampo}>{t.rotulo}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      required
                      value={editando[t.clave]}
                      onChange={(e) => setEditando({ ...editando, [t.clave]: e.target.value })}
                      style={campo}
                    />
                    <span style={{ fontSize: 12, color: c.texto3 }}>{t.ayuda}</span>
                  </label>
                ))}
              </div>
              <AnimatePresence>
                {error && (
                  <motion.div key={error} {...desplegar}>
                    <div role="alert" style={aviso('err')}>{error}</div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="submit" disabled={guardando} style={{ ...botonPrimario, opacity: guardando ? 0.7 : 1 }}>
                  <Cambia>{guardando ? 'Guardando…' : 'Guardar tarifas'}</Cambia>
                </button>
                <button type="button" style={botonSecundario} onClick={() => setEditando(null)}>
                  Cancelar
                </button>
              </div>
            </motion.form>
          )}
          </AnimatePresence>
        </div>
      )}
    </Bloque>
  );
}

/** «2026-09-10» → «jueves 10 de septiembre», para el encabezado. */
export function diaLargo(iso: string): string {
  const fecha = new Date(`${iso}T12:00:00-03:00`);
  if (Number.isNaN(fecha.getTime())) return iso;
  return fecha.toLocaleDateString('es-AR', { timeZone: HUSO, weekday: 'long', day: 'numeric', month: 'long' });
}

/** Tarjeta de sección: título, bajada opcional y el contenido adentro. */
export function Bloque({
  titulo,
  bajada,
  children,
  cuerpo = true,
}: {
  titulo: string;
  bajada?: ReactNode;
  children: ReactNode;
  /** En falso el contenido va pegado al borde: es el caso de las tablas. */
  cuerpo?: boolean;
}) {
  return (
    <motion.div {...aparecer} style={{ ...tarjeta, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 18px 12px' }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>{titulo}</div>
        {bajada && <div style={{ marginTop: 4, fontSize: 12.5, color: c.texto2, textWrap: 'pretty' }}>{bajada}</div>}
      </div>
      <div style={{ flex: 1, padding: cuerpo ? '0 18px 18px' : 0 }}>{children}</div>
    </motion.div>
  );
}

/** Número suelto adentro de una tarjeta, cuando no amerita un `Kpi` propio. */
function Cifra({ rotulo, valor, color }: { rotulo: string; valor: number; color?: string }) {
  return (
    <div className="tablet:min-w-[90px]">
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: c.texto3,
        }}
      >
        {rotulo}
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 26,
          fontWeight: 600,
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums',
          color: color ?? c.texto,
          transition: 'color .3s',
        }}
      >
        <Numero valor={valor} />
      </div>
    </div>
  );
}

export const rejilla = (minimo: number): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit,minmax(min(${minimo}px,100%),1fr))`,
  gap: 16,
  alignItems: 'start',
});

// ------------------------------------------------------- tablas por artículo

/**
 * Ingresos o egresos del día por artículo, ordenados por piezas de mayor a
 * menor, con la barra adentro de la fila. La tabla es la alternativa en
 * texto de la barra: cada barra tiene su número al lado.
 *
 * `contenedor` es opcional y sólo lo llevan los egresos: el mismo artículo
 * puede haber salido de dos contenedores en el mismo día y son dos renglones,
 * porque cada contenedor se cierra por separado. Cuando no viene, la fila
 * queda exactamente como estaba.
 */
function PorArticulo({
  filas,
  color,
  rotuloBultos,
  vacio,
}: {
  filas: {
    articulo: string;
    descripcion: string;
    contenedor?: string | null;
    bultos: number;
    piezas: number;
  }[];
  color: string;
  rotuloBultos: string;
  vacio: { titulo: string; detalle: string; icono: string };
}) {
  const celular = useEsCelular();
  if (filas.length === 0) {
    return <Vacio icono={vacio.icono} titulo={vacio.titulo} detalle={vacio.detalle} />;
  }

  const ordenadas = [...filas].sort((a, b) => b.piezas - a.piezas);
  const maximo = Math.max(...ordenadas.map((f) => f.piezas), 1);

  // En el celular cada artículo es una tarjetita: código y descripción arriba,
  // la barra con las piezas y los bultos abajo. Sin columnas de ancho fijo.
  if (celular) {
    return (
      <div style={{ maxHeight: 440, overflowY: 'auto' }}>
        {ordenadas.map((f, i) => (
          <motion.div
            key={`${f.articulo}·${f.contenedor ?? ''}`}
            {...fila(i)}
            style={{ ...filaTabla, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}
          >
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span style={{ fontFamily: mono, fontSize: 12.5, fontWeight: 500 }}>{f.articulo}</span>
              <span style={{ fontSize: 12, color: c.texto3 }}>{f.descripcion}</span>
              {f.contenedor !== undefined && (
                <span style={{ fontFamily: mono, fontSize: 12, color: c.texto4 }}>{f.contenedor ?? 'sin contenedor'}</span>
              )}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <BarraFila valor={f.piezas} maximo={maximo} color={color} descripcion={`${f.articulo}: ${miles(f.piezas)} piezas`} retraso={i * 0.035} />
              </span>
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                <Numero valor={f.piezas} /> pz
              </span>
            </span>
            <span style={{ fontSize: 12.5, color: c.texto2 }}>
              {rotuloBultos}: <Numero valor={f.bultos} />
            </span>
          </motion.div>
        ))}
      </div>
    );
  }

  // Scroll adentro: con muchos artículos la tabla estiraba la página entera
  // y empujaba todo lo de abajo. El encabezado queda a la vista.
  return (
    <div className="wms-scroll-x" style={{ maxHeight: 440, overflowY: 'auto' }}>
      <div>
        <div style={{ ...encabezadoTabla, position: 'sticky', top: 0, zIndex: 1, background: c.panel }}>
          <span style={{ flex: 1, minWidth: 170 }}>Artículo</span>
          <span style={{ width: 220 }}>Piezas</span>
          <span style={{ width: 90, textAlign: 'right' }}>{rotuloBultos}</span>
        </div>

        {ordenadas.map((f, i) => (
          <motion.div key={`${f.articulo}·${f.contenedor ?? ''}`} {...fila(i)} style={filaTabla}>
            <span style={{ flex: 1, minWidth: 170, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span style={{ fontFamily: mono, fontSize: 12.5, fontWeight: 500 }}>{f.articulo}</span>
              <span style={{ fontSize: 12, color: c.texto3 }}>{f.descripcion}</span>
              {f.contenedor !== undefined && (
                <span style={{ fontFamily: mono, fontSize: 12, color: c.texto4 }}>
                  {f.contenedor ?? 'sin contenedor'}
                </span>
              )}
            </span>
            <span style={{ width: 220, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <BarraFila
                  valor={f.piezas}
                  maximo={maximo}
                  color={color}
                  descripcion={`${f.articulo}: ${miles(f.piezas)} piezas`}
                  retraso={i * 0.035}
                />
              </span>
              <span
                style={{
                  width: 74,
                  textAlign: 'right',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <Numero valor={f.piezas} />
              </span>
            </span>
            <span style={{ width: 90, textAlign: 'right', color: c.texto2, fontVariantNumeric: 'tabular-nums' }}>
              <Numero valor={f.bultos} />
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/**
 * La bitácora de a páginas: en un mes son cientos de movimientos y la página
 * del resumen se volvía interminable.
 */
function Bitacora({ movimientos, esDia }: { movimientos: Resumen['movimientos']; esDia: boolean }) {
  const { visibles, pagina, paginas, total, desde, setPagina } = usePaginado(movimientos);
  const celular = useEsCelular();
  return (
    <>
      {celular ? (
        // Tarjetas: la fila de cinco columnas no entra en 390 px.
        <div>
          {visibles.map((m, i) => {
            const t = tono(aTono(m.tono));
            return (
              <motion.div
                key={`${desde + i}`}
                {...fila(i)}
                style={{ ...filaTabla, flexDirection: 'column', alignItems: 'stretch', gap: 4 }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      flex: '0 0 26px',
                      borderRadius: 8,
                      display: 'grid',
                      placeItems: 'center',
                      background: t.bg,
                      border: `1px solid ${t.bd}`,
                      color: t.fg,
                    }}
                  >
                    <Icono nombre={m.icono || 'swap_horiz'} size={16} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 500 }}>{m.tipo}</span>
                  <span style={{ fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>{m.hora}</span>
                </span>
                <span style={{ color: c.texto2 }}>{m.detalle}</span>
                {(m.posicion || m.operario) && (
                  <span style={{ fontSize: 12.5, color: c.texto3 }}>
                    {[m.posicion, m.operario].filter(Boolean).join(' · ')}
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>
      ) : (
      <div className="wms-scroll-x">
        <div>
          <div style={encabezadoTabla}>
            <span style={{ width: esDia ? 70 : 110 }}>{esDia ? 'Hora' : 'Cuándo'}</span>
            <span style={{ width: 190 }}>Tipo</span>
            <span style={{ flex: 1, minWidth: 200 }}>Detalle</span>
            <span style={{ width: 110 }}>Posición</span>
            <span style={{ width: 140 }}>Operario</span>
          </div>

          {visibles.map((m, i) => {
            const t = tono(aTono(m.tono));
            return (
              <motion.div key={`${desde + i}`} {...fila(i)} style={filaTabla}>
                <span
                  style={{ width: esDia ? 70 : 110, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}
                >
                  {m.hora}
                </span>
                <span style={{ width: 190, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      flex: '0 0 26px',
                      borderRadius: 8,
                      display: 'grid',
                      placeItems: 'center',
                      background: t.bg,
                      border: `1px solid ${t.bd}`,
                      color: t.fg,
                    }}
                  >
                    <Icono nombre={m.icono || 'swap_horiz'} size={16} />
                  </span>
                  <span style={{ minWidth: 0, fontWeight: 500 }}>{m.tipo}</span>
                </span>
                <span style={{ flex: 1, minWidth: 200, color: c.texto2 }}>{m.detalle}</span>
                <span style={{ width: 110, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                  {m.posicion || '—'}
                </span>
                <span style={{ width: 140, color: c.texto2 }}>{m.operario || '—'}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
      )}
      <Paginador pagina={pagina} paginas={paginas} total={total} desde={desde} que="movimientos" onIr={setPagina} />
    </>
  );
}

// ------------------------------------------------------------------ pantalla

export default function ResumenDiario() {
  // Vacío significa «el día que el servidor considere hoy»: así la pantalla
  // arranca sin tener que adivinar la fecha del depósito.
  const [dia, setDia] = useState('');
  const [periodo, setPeriodo] = useState<Periodo>('dia');
  const [descargando, setDescargando] = useState(false);
  const [resultado, setResultado] = useState<{ texto: string; ok: boolean } | null>(null);

  const { datos, cargando, error, recargar } = useApi<Resumen>(() => api.resumen(periodo, dia || undefined), [periodo, dia]);

  async function descargar(r: Resumen) {
    setDescargando(true);
    setResultado(null);
    try {
      await api.descargarResumenCsv(r.periodo, r.dia);
      setResultado({ texto: `Se descargó: ${tituloDe(r)}.`, ok: true });
    } catch (err) {
      setResultado({ texto: mensajeDeError(err), ok: false });
    } finally {
      setDescargando(false);
    }
  }

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (!datos) return <Cargando texto="Armando el resumen…" />;

  // «del día» o «del período», para los rótulos que antes eran sólo del día.
  const esDia = datos.periodo === 'dia';
  const del = esDia ? 'del día' : 'del período';
  const este = esDia ? 'Este día' : 'El período';

  const { kpis, stock, ordenes, capachos } = datos;

  const piezasCapachos = capachos.reduce((s, e) => s + e.piezas, 0);
  const hayCapachos = capachos.some((e) => e.cantidad > 0);
  const maxCapachos = Math.max(...capachos.map((e) => e.cantidad), 1);

  const stockPartes = [
    // Orden validado para daltonismo: verde → azul → ámbar. Ver `graficos.tsx`.
    { nombre: 'Stock RDM', valor: stock.rdm, color: PALETA.entra },
    { nombre: 'Stock ZF', valor: stock.zf, color: PALETA.sale },
    { nombre: 'Sin paletizar', valor: stock.sinPaletizar, color: PALETA.falta },
  ];
  const stockTotal = stockPartes.reduce((s, p) => s + p.valor, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        titulo={tituloDe(datos)}
        bajada={`Movimientos, ingresos, egresos, stock y órdenes ${del}, para administración.`}
      >
        <button
          type="button"
          onClick={() => void descargar(datos)}
          disabled={descargando}
          style={{ ...botonSecundario, opacity: descargando ? 0.7 : 1 }}
        >
          <Icono nombre="download" size={19} />
          <Cambia>{descargando ? 'Generando…' : 'Descargar CSV'}</Cambia>
        </button>
      </Encabezado>

      <div role="group" aria-label="Período" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {PERIODOS.map((p) => (
          <ChipBoton key={p.periodo} grupo="resumen-periodo" activo={periodo === p.periodo} onClick={() => setPeriodo(p.periodo)}>
            {p.rotulo}
          </ChipBoton>
        ))}
      </div>

      {/* Una sola fila de controles, arriba de todo lo que filtran. «Todo» no
          tiene vecinos ni fecha que elegir. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {datos.periodo !== 'todo' && (
          <>
            <button
              type="button"
              onClick={() => datos.anterior && setDia(datos.anterior)}
              // Null en el primer período con datos: más atrás está todo vacío.
              disabled={datos.anterior === null}
              aria-label="Período anterior"
              style={{
                ...botonSecundario,
                padding: '10px 14px',
                opacity: datos.anterior === null ? 0.45 : 1,
                cursor: datos.anterior === null ? 'default' : 'pointer',
              }}
            >
              <Icono nombre="chevron_left" size={20} />
            </button>

            {/* En el celular el rótulo va arriba del campo y la fecha se estira entre las
                flechas: así «‹ fecha › Hoy» entra en un renglón y «Hoy» no queda suelto. */}
            <label className="flex min-w-0 flex-1 flex-col gap-1 tablet:flex-none tablet:flex-row tablet:items-center tablet:gap-2">
              <span style={{ fontSize: 13, color: c.texto2 }}>{esDia ? 'Día' : 'Incluye el día'}</span>
              <input
                type="date"
                value={datos.dia}
                onChange={(e) => setDia(e.target.value)}
                className="min-w-0 tablet:min-w-40"
                style={{ ...campo, width: 'auto' }}
              />
            </label>

            <button
              type="button"
              onClick={() => datos.siguiente && setDia(datos.siguiente)}
              disabled={datos.siguiente === null}
              aria-label="Período siguiente"
              style={{
                ...botonSecundario,
                padding: '10px 14px',
                opacity: datos.siguiente === null ? 0.45 : 1,
                cursor: datos.siguiente === null ? 'default' : 'pointer',
              }}
            >
              <Icono nombre="chevron_right" size={20} />
            </button>

            <ChipBoton grupo="resumen-hoy" activo={datos.hoy} onClick={() => setDia('')} disabled={datos.hoy}>
              {esDia ? 'Hoy' : 'Actual'}
            </ChipBoton>
          </>
        )}

        <AnimatePresence>
          {cargando && (
            <motion.span
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              style={{ fontSize: 12.5, color: c.texto3 }}
              role="status"
            >
              Actualizando…
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {resultado && (
          <motion.div key={resultado.texto} {...abrir(16)} role={resultado.ok ? 'status' : 'alert'} style={aviso(resultado.ok ? 'ok' : 'err')}>
            {resultado.texto}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mientras recarga se mantiene el dibujo anterior atenuado: sin salto
          de layout y sin esqueleto parpadeando. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          opacity: cargando ? 0.55 : 1,
          transition: 'opacity .15s',
        }}
      >
        {datos.facturacion && <Facturacion f={datos.facturacion} onCambio={recargar} />}

        <div style={rejilla(200)}>
          <motion.div {...fila(0)}>
          <Kpi rotulo="Pallets ingresados" valor={kpis.palletsIngresados} pie="entraron al depósito" />
          </motion.div>
          <motion.div {...fila(1)}>
          <Kpi rotulo="Piezas ingresadas" valor={kpis.piezasIngresadas} pie="unidades recibidas" />
          </motion.div>
          <motion.div {...fila(2)}>
          <Kpi rotulo="Capachos entregados" valor={kpis.capachosEntregados} pie="armados y despachados" />
          </motion.div>
          {/* Los pallets que salieron enteros no son capachos: es otra
              operación, cuesta otra cosa y se factura aparte. */}
          <motion.div {...fila(3)}>
          <Kpi
            rotulo="Pallets despachados"
            valor={kpis.palletsDespachados}
            pie="salieron completos"
          />
          </motion.div>
          <motion.div {...fila(4)}>
          <Kpi rotulo="Piezas entregadas" valor={kpis.piezasEntregadas} pie="unidades despachadas" />
          </motion.div>
          <motion.div {...fila(5)}>
          <Kpi
            rotulo="Movimientos"
            valor={kpis.movimientos}
            pie={kpis.movimientos === 0 ? `${este.toLowerCase()} no tuvo actividad` : `registrados ${esDia ? 'en el día' : 'en el período'}`}
            pieColor={kpis.movimientos === 0 ? c.texto3 : undefined}
          />
          </motion.div>
        </div>

        {/*
          Dos líneas sobre un solo eje: el trabajo del lector es ver la
          tendencia de catorce días y comparar entrada contra salida, y eso es
          exactamente lo que hace una línea por serie. Los movimientos van a la
          tabla y no al dibujo, porque un segundo eje inventaría una relación
          que en los datos no está.
        */}
        <Bloque
          titulo={esDia ? 'Últimos 14 días' : datos.serie[0]?.dia.length === 7 ? 'Mes por mes' : 'Día por día'}
          bajada="Piezas ingresadas contra piezas entregadas. Pasá el mouse —o usá las flechas ← → con el gráfico enfocado— para ver los valores de cada punto."
        >
          <Lineas
            etiquetas={datos.serie.map((s) => diaCorto(s.dia))}
            series={[
              { nombre: 'Piezas ingresadas', color: PALETA.entra, valores: datos.serie.map((s) => s.piezasIngresadas) },
              { nombre: 'Piezas entregadas', color: PALETA.sale, valores: datos.serie.map((s) => s.piezasEntregadas) },
            ]}
            soloEnTabla={[{ nombre: 'Movimientos', valores: datos.serie.map((s) => s.movimientos) }]}
            descripcion={`Piezas ingresadas y entregadas, ${datos.serie.length} puntos. Los valores exactos están en la tabla que sigue.`}
          />
        </Bloque>

        <div style={rejilla(320)}>
          {/*
            Barra apilada: son tres partes de un mismo total —el stock del
            depósito— y lo que hay que leer de un vistazo es la proporción
            entre ellas, no cada valor por separado.
          */}
          <Bloque
            titulo="Stock RDM y stock ZF"
            bajada={
              <>
                Cuánto hay guardado todavía a nombre de RDM, cuánto ya pasó a ZF y cuánto falta paletizar.{' '}
                <strong style={{ color: c.texto }}>Es la foto de hoy, no la {esDia ? 'del día elegido' : 'del período'}.</strong>
              </>
            }
          >
            <BarraApilada
              partes={stockPartes}
              descripcion={`Reparto del stock: ${stockPartes
                .map((p) => `${p.nombre} ${miles(p.valor)}`)
                .join(', ')}. Total ${miles(stockTotal)}.`}
              vacio="No hay stock cargado."
            />
            <div style={{ marginTop: 14, fontSize: 12.5, color: c.texto2 }}>
              Total <Numero valor={stockTotal} /> · <Numero valor={kpis.palletsEnDeposito} /> pallets en depósito.
            </div>
          </Bloque>

          {/*
            Medidor: una sola razón contra su límite. Un gráfico de dos rebanadas
            sería ruido; el número y la barra alcanzan.
          */}
          <Bloque
            titulo="Ocupación"
            bajada={
              <>
                Posiciones ocupadas sobre el total del depósito.{' '}
                <strong style={{ color: c.texto }}>Es la foto de hoy, no la {esDia ? 'del día elegido' : 'del período'}.</strong>
              </>
            }
          >
            <Medidor
              valor={stock.posicionesOcupadas}
              total={stock.posicionesTotales}
              rotulo="Posiciones ocupadas"
              pie={`${miles(stock.posicionesOcupadas)} de ${miles(stock.posicionesTotales)} posiciones · quedan ${miles(
                Math.max(stock.posicionesTotales - stock.posicionesOcupadas, 0),
              )} libres.`}
            />
          </Bloque>
        </div>

        {/* 620 y no menos: las tablas de `.wms-scroll-x` no bajan de 720 px, así
            que en dos columnas angostas cada una scrollearía al pedo. */}
        <div style={rejilla(620)}>
          {/* Barras horizontales adentro de la tabla: comparar magnitudes entre
              artículos, con el número exacto en la misma fila. */}
          <Bloque titulo={`Ingresos ${del} por artículo`} bajada="Ordenado por piezas, de mayor a menor." cuerpo={false}>
            <PorArticulo
              filas={datos.ingresos.map((i) => ({ ...i, bultos: i.pallets }))}
              color={PALETA.entra}
              rotuloBultos="Pallets"
              vacio={{
                icono: 'pallet',
                titulo: 'Sin ingresos',
                detalle: `${este} no tuvo ingresos: no entró ningún pallet al depósito.`,
              }}
            />
          </Bloque>

          <Bloque
            titulo={`Egresos ${del} por artículo`}
            bajada="Un renglón por artículo y contenedor, para poder cerrar cada contenedor por separado."
            cuerpo={false}
          >
            <PorArticulo
              filas={datos.egresos.map((e) => ({ ...e, bultos: e.capachos + e.pallets }))}
              color={PALETA.sale}
              rotuloBultos="Salidas"
              vacio={{
                icono: 'outbox',
                titulo: 'Sin egresos',
                detalle: `${este} no tuvo egresos: no salió nada del depósito.`,
              }}
            />
          </Bloque>
        </div>

        <div style={rejilla(320)}>
          <Bloque
            titulo="Capachos por estado"
            bajada={
              <>
                <strong style={{ color: c.texto }}>Es lo pendiente de hoy, no lo {del}.</strong> Cambiar de fecha
                arriba no cambia estos números.
              </>
            }
          >
            {!hayCapachos ? (
              <Vacio
                icono="call_split"
                titulo="Sin capachos pendientes"
                detalle="Ahora mismo no hay capachos pedidos, aceptados ni entregados sin cerrar."
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {capachos.map((e, i) => {
                  // El estado ya tiene tono asignado en el sistema: entregado
                  // es el final feliz, aceptado es «hay algo que hacer».
                  const t = tono(tonoDeEstado(e.estado));
                  return (
                    <div key={e.estado}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: c.texto }}>{e.estado}</span>
                        <span style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          <Numero valor={e.cantidad} />
                        </span>
                        <span style={{ fontSize: 12.5, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}>
                          <Numero valor={e.piezas} /> pz
                        </span>
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <BarraFila
                          valor={e.cantidad}
                          maximo={maxCapachos}
                          color={t.fg}
                          descripcion={`${e.estado}: ${miles(e.cantidad)} capachos, ${miles(e.piezas)} piezas`}
                          retraso={i * 0.08}
                        />
                      </div>
                    </div>
                  );
                })}
                <div style={{ fontSize: 12.5, color: c.texto2 }}>
                  <Numero valor={piezasCapachos} /> piezas en total entre los tres estados.
                </div>
              </div>
            )}
          </Bloque>

          <Bloque titulo="Órdenes de trabajo" bajada={`Las abiertas son el pendiente de hoy; las otras dos, ${esDia ? 'del día elegido' : 'del período'}.`}>
            <div className="flex flex-wrap gap-3 tablet:gap-6">
              <Cifra rotulo="Abiertas" valor={ordenes.abiertas} color={ordenes.abiertas > 0 ? c.ambar : undefined} />
              <Cifra rotulo="Creadas" valor={ordenes.creadasHoy} />
              <Cifra rotulo="Despachadas" valor={ordenes.despachadasHoy} color={c.verde} />
            </div>
          </Bloque>
        </div>

        <Bloque
          titulo={`Bitácora ${del}`}
          bajada={
            // El servidor manda los últimos 200: en un mes, la lista entera no se lee.
            datos.movimientos.length >= 200
              ? `Los últimos 200 de ${miles(kpis.movimientos)} movimientos, del más nuevo al más viejo.`
              : `${miles(datos.movimientos.length)} ${
                  datos.movimientos.length === 1 ? 'movimiento registrado' : 'movimientos registrados'
                }, del más nuevo al más viejo.`
          }
          cuerpo={false}
        >
          {datos.movimientos.length === 0 ? (
            <Vacio
              icono="history"
              titulo="Sin movimientos"
              detalle={`${este} no tuvo movimientos: nadie ingresó, movió ni entregó nada.`}
            />
          ) : (
            <Bitacora movimientos={datos.movimientos} esDia={esDia} />
          )}
        </Bloque>
      </div>
    </div>
  );
}
